import { useEffect, useState } from 'react'
import { myLanes } from '../lib/support'
import { AppShell } from '../components/AppShell'
import { supabase } from '../lib/supabase'
import { can, canGallery, type Me } from '../lib/me'
import { canReachShopAdmin } from '../lib/shop'
import { useHubLang } from '../lib/i18n'
import { CARD, GUIDE_LINKS, PROFILE, type ProfileCopy } from '../lib/hubCopy'
import { GuideLink } from '../components/guides/GuideLink'
import { OpenCardButton } from '../components/BusinessCard'
import { CardEditDialog } from '../components/CardEditDialog'
import { PhotoCropDialog } from '../components/PhotoCropDialog'
import { PhotosOfMe } from '../components/PhotosOfMe'
import { useMyPhotoFlow } from '../components/useMyPhotoFlow'
import { EmailsSection } from '../components/EmailsSection'
import { CARD_RELEASED } from '../lib/businessCard'
import { initialsOf } from '../lib/initials'
import {
  LANGUAGE_HEADING,
  SUPPORTED_LOCALES,
  isSupportedLocale,
  languageNote,
  readingChoices,
  setMyLocale,
  setMyReadingLocales,
  type SupportedLocale,
} from '../lib/locale'
import { loadSignatureCard, locationLine, placeLines, type SignatureCard } from '../lib/signature'

/**
 * Your profile — SHL-14, reworked 11 Oct (David).
 *
 * ── WHAT IS HERE, TOP TO BOTTOM ─────────────────────────────────────────────
 *
 *   1. You: your photo (yours to change), your name and title, and what
 *      the company holds (BambooHR's fields, read-only - ACC-33 below). One
 *      card since 30 Oct: the header and a separate "What the company holds"
 *      card repeated the same four facts.
 *   2. Language and translation: the hub's language, and the languages you
 *      read as written (168). The picker MOVED HERE from the identity menu on
 *      11 Oct (David): settings live on the settings page, and the menu is
 *      for going places.
 *   3. Your digital card: the optional details only the card carries —
 *      mobile, office phone, office address — and the way to show it.
 *   4. Photos you are in — only once somebody has tagged you (PhotosOfMe).
 *
 * ── TWO COLUMNS ON A WIDE SCREEN (30 Oct, David: "improve the UI") ──────────
 *
 * Left: who you are (you, your card, photos). Right: how the hub works for
 * you (language, emails). One column, in that DOM order, below 85rem - so a
 * screen reader and a phone read the same sequence.
 *
 * ── WHAT LEFT ON 11 OCT ─────────────────────────────────────────────────────
 *
 * The email signature, including its "coming later" card. David: "remove
 * e-sig for now and we'll put this back in in 2027." The generator is NOT
 * deleted: lib/signature.ts (buildSignatureHtml, SIGNATURE_RELEASED) and
 * components/SignaturePreview.tsx keep it, and bringing it back is a new
 * section here plus Marketing's six differences (docs/email-signature-
 * template.md). Until then the Brand Transition Q&A's "Not yet" (9.4) is the
 * only answer anyone is given, which is what OPS-57 wanted.
 *
 * ── THE SCREEN IS HONEST ABOUT ACC-33 ───────────────────────────────────────
 *
 * Everything the company holds is rewritten from BambooHR at 04:00 Pacific by
 * `set_employment`'s REPLACE, and nothing records that it was ever different.
 * So those fields are not editable here — an editor that silently loses its
 * work overnight is worse than a read-only panel that says where to go. The
 * card's phones, address and photo are the exception because they are
 * self-entered and the sync has never heard of them.
 *
 * ── TIME ZONE IS NOT HERE, ON PURPOSE (David, 11 Oct) ───────────────────────
 *
 * The hub's time zone comes from your site (app.resolve_person_timezone), and
 * it is used for the overnight quiet hours and the countdown. An override
 * helps only somebody on a long assignment abroad; parked in IDEAS.md.
 */
export function Profile({ me }: { me: Me }) {
  const lang = useHubLang()
  const t = PROFILE[lang]
  const c = CARD[lang]
  const [card, setCard] = useState<SignatureCard | null>(null)
  const [editOpen, setEditOpen] = useState(false)
  const flow = useMyPhotoFlow(card !== null, c)

  useEffect(() => {
    let live = true
    loadSignatureCard()
      .then((fresh) => live && setCard(fresh))
      // A refusal (no company address) leaves the card section with only
      // Show my card, whose dialog says why; nothing else here needs it.
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [])

  async function reloadCard() {
    setCard(await loadSignatureCard())
  }

  const place = card ? locationLine(card) : ''

  return (
    <AppShell
      current="Your profile"
      eyebrow={t.eyebrow}
      canAdmin={can(me, 'admin.roles')}
      canShop={canReachShopAdmin(me)}
      canModerate={can(me, 'content.moderate')}
      canStarsAdmin={can(me, 'stars.admin')}
      canPhotoRoom={canGallery(me)}
      canAnalytics={can(me, 'analytics.read')}
      canNotices={can(me, 'content.publish')}
      canAudit={can(me, 'audit.read')}
      supportLanes={myLanes(me)}
      userName={me.displayName}
      userInitials={initialsOf(me.displayName)}
      userRole={me.roles[0]?.name ?? 'No role'}
      locale={me.locale}
      onSignOut={() => supabase.auth.signOut()}
    >
      {/* No <h1> here: AppShell already renders one from `current`, and a
          second would be two page titles to a screen reader. */}

      <div className="profile">
        <div className="profile__col">
          {/* 1. You, and what the company holds */}
          <section className="card profile__card" aria-labelledby="me-h">
            <div className="profile__head">
              <div className="profile__face" aria-hidden="true">
                {flow.photoUrl ? <img src={flow.photoUrl} alt="" /> : initialsOf(me.displayName)}
              </div>
              <div className="profile__who">
                <h2 id="me-h" className="profile__name">{card?.displayName ?? me.displayName}</h2>
                {card?.jobTitle && <p className="profile__role">{card.jobTitle}</p>}
                {/* No photo button until my_photo() has answered (SHL-8): a button
                    that cannot upload is a control that looks like it works. */}
                {flow.photo && (
                  <div className="profile__photobtns">
                    <button
                      type="button"
                      className="btn btn--quiet btn--sm"
                      disabled={flow.photoBusy || flow.cropSource !== null}
                      onClick={flow.pick}
                    >
                      {flow.photoBusy ? c.photoSaving : flow.photoUrl ? c.changePhoto : c.addPhoto}
                    </button>
                    {flow.photoUrl && !flow.photoBusy && (
                      <button type="button" className="btn btn--link" onClick={() => void flow.onRemovePhoto()}>
                        {c.removePhoto}
                      </button>
                    )}
                  </div>
                )}
                {flow.photo && <p className="profile__note profile__note--tight">{t.photoOnlyOnCard}</p>}
                {flow.photoError && !editOpen && <p className="profile__error" role="alert">{flow.photoError}</p>}
              </div>
            </div>

            {/* What the company holds. The name and title are the heading
                above, so they are not listed twice. */}
            {card && (
              <div className="profile__record">
                <h3 className="profile__subhead">{t.whatWeHold}</h3>
                <dl className="profile__recordgrid">
                  <Fact label={t.legalEntity}>{card.legalEntity ?? '—'}</Fact>
                  <Fact label={t.location}>{place || '—'}</Fact>
                  <Fact label={t.workEmail}>{card.workEmail ?? '—'}</Fact>
                </dl>
                <p className="profile__note profile__note--tight">{t.factsNote}</p>
              </div>
            )}
          </section>

          {/* 3. The card. Outside `{card && …}` on purpose: the dialog fetches for
              itself and explains its own refusals, so somebody with no company
              address still finds the card and is told why. */}
          {CARD_RELEASED && (
            <section className="card profile__card" aria-labelledby="vcard-h">
              <h2 id="vcard-h" className="card__title">
                {t.cardHead}
              </h2>
              <p className="profile__lede">{t.cardLede}</p>
              <GuideLink guide="getting-started" section="profile-and-card" label={GUIDE_LINKS[lang].profileCard} />
              {card && (
                <dl className="profile__facts profile__facts--compact">
                  <Fact label={c.mobileField}>{card.workPhone || <Missing t={t} />}</Fact>
                  <Fact label={c.officePhoneField}>{card.officePhone || <Missing t={t} />}</Fact>
                  <Fact label={c.officeAddressHead}>
                    {card.office ? placeLines(card).join(', ') : <Missing t={t} />}
                  </Fact>
                </dl>
              )}
              <div className="profile__actions">
                <OpenCardButton />
                {card && (
                  <button type="button" className="btn btn--quiet" onClick={() => setEditOpen(true)} aria-haspopup="dialog">
                    {c.editCard}
                  </button>
                )}
              </div>
            </section>
          )}

          {/* 4. Photos you are in — renders nothing until somebody tags you. */}
          <PhotosOfMe />
        </div>

        <div className="profile__col">
          {/* 2. Language and translation */}
          <LanguageSection me={me} />

          {/* 2b. Emails and notifications (326, ACC-53), beside Language. */}
          <EmailsSection />
        </div>
      </div>

      {/* SIBLINGS of the sections, never inside a dialog's tree (PhotoCrop-
          Dialog's header). The file input is shared by both photo buttons. */}
      <input
        ref={flow.inputRef}
        hidden
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic"
        onChange={(e) => void flow.onPickPhoto(e.target.files?.[0])}
      />
      <CardEditDialog
        open={editOpen}
        card={card}
        photoUrl={flow.photoUrl}
        photoReady={flow.photo !== null}
        photoBusy={flow.photoBusy || flow.cropSource !== null}
        photoError={flow.photoError}
        onChangePhoto={flow.pick}
        onRemovePhoto={() => void flow.onRemovePhoto()}
        onSaved={reloadCard}
        onClose={() => {
          setEditOpen(false)
          flow.clearError()
        }}
        t={c}
      />
      <PhotoCropDialog
        source={flow.cropSource}
        busy={flow.cropBusy}
        error={flow.cropError}
        onCancel={flow.closeCrop}
        onUse={(crop) => void flow.onUseCrop(crop)}
        t={c}
      />
    </AppShell>
  )
}

/**
 * The hub's language, and the languages read as written (168).
 *
 * The language saves and RELOADS: `me.locale` is read once at sign-in (App's
 * loadMe) and the feed, the drawer and every variant resolve from it, so a
 * soft update would leave half the screen in the old language.
 *
 * Reading languages save on each tick, and need no reload for the NEXT thing
 * read; what is already on screen was resolved before the tick.
 */
function LanguageSection({ me }: { me: Me }) {
  const lang = useHubLang()
  const t = PROFILE[lang]
  const locale = isSupportedLocale(me.locale) ? me.locale : 'en-CA'
  const [savingLocale, setSavingLocale] = useState(false)
  const [localeError, setLocaleError] = useState<string | null>(null)
  const [kept, setKept] = useState<string[]>(me.readAsWritten ?? [])
  const [readState, setReadState] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle')
  const choices = readingChoices(locale)

  async function onLocale(next: string) {
    if (!isSupportedLocale(next) || next === locale) return
    setSavingLocale(true)
    setLocaleError(null)
    try {
      await setMyLocale(next)
      window.location.reload()
    } catch {
      setLocaleError(t.languageFailed)
      setSavingLocale(false)
    }
  }

  async function onRead(key: SupportedLocale, on: boolean) {
    const before = kept
    // Only the boxes on screen are sent: an entry equal to your own language
    // would do nothing, and the server keeps the list it is given (REPLACE).
    const next = choices.map((l) => l.key).filter((k) => (k === key ? on : before.includes(k)))
    setKept(next)
    setReadState('saving')
    try {
      setKept(await setMyReadingLocales(next))
      setReadState('saved')
    } catch {
      setKept(before)
      setReadState('failed')
    }
  }

  return (
    <section className="card profile__card" aria-labelledby="lang-h">
      <h2 id="lang-h" className="card__title">
        {t.languageHead}
      </h2>
      <GuideLink guide="getting-started" section="language" label={GUIDE_LINKS[lang].language} />
      <label className="profile__field" htmlFor="profile-language">
        {/* Bilingual on purpose: somebody who cannot read the English around
            it still has to be able to find their own language. */}
        <span className="profile__fieldlabel">{LANGUAGE_HEADING}</span>
        <select
          id="profile-language"
          className="profile__input"
          value={locale}
          disabled={savingLocale}
          onChange={(e) => void onLocale(e.target.value)}
        >
          {SUPPORTED_LOCALES.map((l) => (
            <option key={l.key} value={l.key}>
              {l.label}
            </option>
          ))}
        </select>
      </label>
      <p className="profile__note profile__note--tight">{languageNote(locale)}</p>
      {localeError && <p className="profile__error" role="alert">{localeError}</p>}

      {choices.length > 0 && (
        <fieldset className="profile__read">
          <legend className="profile__subhead">{t.readHead}</legend>
          <p className="profile__lede">{t.readLede}</p>
          {choices.map((l) => (
            <label key={l.key} className="profile__check">
              <input
                type="checkbox"
                checked={kept.includes(l.key)}
                disabled={readState === 'saving'}
                onChange={(e) => void onRead(l.key, e.target.checked)}
              />
              <span>{t.readOption(l.label)}</span>
            </label>
          ))}
          <p className="profile__status" role="status" aria-live="polite">
            {readState === 'saved' ? t.readSaved : ''}
          </p>
          {readState === 'failed' && <p className="profile__error" role="alert">{t.readFailed}</p>}
        </fieldset>
      )}
    </section>
  )
}

function Missing({ t }: { t: ProfileCopy }) {
  return <span className="profile__missing">{t.cardDetailsNone}</span>
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    // .facts__row/__label/__value are app.css's, already used by Home. Reused
    // rather than restyled — rule 11 applies to a label/value pair as much as
    // to a function. `--compact` (hub.css) only tightens the spacing.
    <div className="facts__row">
      <dt className="facts__label">{label}</dt>
      <dd className="facts__value">{children}</dd>
    </div>
  )
}
